import React, { ComponentPropsWithoutRef, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';

import { AnimatePresence } from '@adsk/alloy-react-animation';
import { useForwardedRef, variantSwitch } from '@adsk/alloy-react-helpers';
import Portal from '@adsk/alloy-react-portal';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import {
  Overlay,
  Header,
  Body,
  Footer,
  Close,
  PrevArrow,
  NextArrow,
} from './layout';
import ModalContext from './ModalContext';

/**
 * This is here instead of inline to avoid having to disable jsx-a11y lint rules which report onClick.
 * In this case, we want to stop propagation of the event to prevent child overlays from closing the modal
 */
const eventPreventionProps = {
  onClick: (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation(),
};

const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  EXTRA_LARGE: 'extra_large',
} as const;

type Sizes = typeof SIZES;
export type Size = Sizes[keyof Sizes] | undefined;

type OverlayProps = ComponentPropsWithoutRef<typeof Overlay>;
type ModalProps = {
  open?: boolean;
  width?: string | number;
  height?: string | number;
  container?: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  hideOverlay?: boolean;
  onClose?: (e?: React.MouseEvent) => void;
  shouldCloseOnEsc?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  size?: Size;
} & Pick<
  OverlayProps,
  'onAnimationStart' | 'onAnimationComplete' | 'initial' | 'animate' | 'exit'
> & {
    overlayStyle?: OverlayProps['style'];
  };

/**
 * A modal is an overlay that focuses the customer's attention on a single task or set of controls.
 * It creates a mode that disables the main window but keeps it visible with the modal window as a child window in front of it.
 * Users must interact with the modal window before they can return to the parent application.
 */
const Modal = React.forwardRef<
  HTMLDivElement,
  ModalProps & Omit<StylableComponent<HTMLDivElement>, 'onAnimationStart'>
>(
  (
    {
      children,
      className,
      style,
      open,
      height,
      width,
      container = document.body,
      hideOverlay,
      onClose,
      onAnimationStart,
      onAnimationComplete,
      overlayStyle,
      shouldCloseOnEsc = true,
      shouldCloseOnOverlayClick = true,
      size = SIZES.MEDIUM,
      initial,
      animate,
      exit,
      ...props
    },
    ref,
  ) => {
    const modalRef = useForwardedRef(ref);

    const onClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
      (e) => {
        if (modalRef.current.contains(e.target as Node)) {
          // Don't close the modal if the click is inside the modal
          return;
        }

        shouldCloseOnOverlayClick && onClose?.(e);
      },
      [modalRef, shouldCloseOnOverlayClick, onClose],
    );

    const onEscape = useCallback(
      (e: KeyboardEvent) => {
        onClose?.();
        e.stopPropagation();
      },
      [onClose],
    );

    const context = useMemo(() => ({ shouldCloseOnEsc }), [shouldCloseOnEsc]);

    return (
      <Portal container={container}>
        <AnimatePresence>
          {open && (
            <Overlay
              data-testid="modal-overlay"
              hide={hideOverlay}
              onClick={onClick}
              onAnimationStart={onAnimationStart}
              onAnimationComplete={onAnimationComplete}
              initial={initial}
              animate={animate}
              exit={exit}
              style={overlayStyle}
              onEscape={shouldCloseOnEsc ? onEscape : undefined}
            >
              <div
                tabIndex={-1}
                {...props}
                className={className}
                {...eventPreventionProps}
                ref={modalRef}
                css={[
                  {
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: 'calc(100vw - 32px)',
                    width:
                      width ||
                      variantSwitch({
                        statement: size,
                        variants: {
                          [SIZES.SMALL]: 420,
                          [SIZES.MEDIUM]: 600,
                          [SIZES.LARGE]: 800,
                          [SIZES.EXTRA_LARGE]: 1140,
                        },
                      }) ||
                      undefined,
                    maxHeight: 'calc(100vh - 120px)',
                    height:
                      height ||
                      variantSwitch({
                        statement: size,
                        variants: {
                          [SIZES.SMALL]: 359,
                          [SIZES.MEDIUM]: 480,
                          [SIZES.LARGE]: 644,
                          [SIZES.EXTRA_LARGE]: 733,
                        },
                      }) ||
                      undefined,
                    outline: 'none',
                    backgroundColor:
                      theme.tokens.colors.background.primary.value,
                    borderRadius: theme.borderRadius.medium,
                    boxShadow: 'rgba(0, 0, 0, 0.25) 0px 0px 8px 0px',
                    zIndex: theme.zIndex.modal,

                    ...(hideOverlay && {
                      pointerEvents: 'all',
                    }),
                  },
                  style,
                ]}
              >
                <ModalContext.Provider value={context}>
                  {children}
                </ModalContext.Provider>
              </div>
            </Overlay>
          )}
        </AnimatePresence>
      </Portal>
    );
  },
);

Modal.displayName = 'Modal';

Modal.propTypes = {
  /** The content of modal */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** is modal open */
  open: PropTypes.bool,
  /** modal width, default is 400px */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** modal height, default is 400px */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Container Used for attaching the modal as a child component to the container */
  container: PropTypes.object,
  /** hide modal Overlay */
  hideOverlay: PropTypes.bool,
  /** called on click close modal */
  onClose: PropTypes.func,
  /** A callback that's called when the modal has started open animation */
  onAnimationStart: PropTypes.func,
  /** A callback that's called when the modal has Complete Animation close animation */
  onAnimationComplete: PropTypes.func,
  /** Set whether modal should be closed when pressing on esc button */
  shouldCloseOnEsc: PropTypes.bool,
  /** Set whether modal should be closed when click on overlay */
  shouldCloseOnOverlayClick: PropTypes.bool,
  /** Modal size */
  size: PropTypes.oneOf(Object.values(SIZES)),
};

export default Object.assign(Modal, {
  Overlay,
  Header,
  Body,
  Footer,
  Close,
  PrevArrow,
  NextArrow,
  SIZES,
});
